import { useEffect, useState } from 'react';
import ContactForm from '../../components/ContactForm';
import StoreBadges from '../../components/StoreBadges';
import usePageMeta from '../../utils/usePageMeta';
import './Home.css';

const FEATURES = [
  {
    color: 'blue',
    icon: '/icons/desktop/svg-8.svg',
    title: 'Liquidación Automática de Expensas',
    desc: 'Generá liquidaciones mensuales en segundos. El sistema calcula automáticamente los importes según los coeficientes de cada unidad, sin errores.',
  },
  {
    color: 'green',
    icon: '/icons/desktop/svg-4.svg',
    title: 'Sistemas de cobranzas',
    desc: 'Registrá cobros con las diferentes plataformas (Banco Roela, Pago mi Expensa), envía avisos de vencimiento automáticos y consultá el historial de pagos desde cualquier dispositivo.',
  },
  {
    color: 'violet',
    icon: '/icons/desktop/svg-3.svg',
    title: 'Portal del Propietario',
    desc: 'Cada propietario accede a su portal personal para ver sus expensas, historial de pagos, documentos del consorcio y novedades del edificio.',
  },
  {
    color: 'orange',
    icon: '/icons/desktop/svg-2.svg',
    title: 'Mantenete comunicado',
    desc: 'Desde el módulo de comunicaciones podés informar asambleas, reuniones, cortes de luz y agua programados y cualquier notificación que necesites que los propietarios e inquilinos tengan presente.',
  },
  {
    color: 'red',
    icon: '/icons/desktop/svg-1.svg',
    title: 'Balances y Reportes en Tiempo Real',
    desc: 'Generá balances contables, informes de morosidad y reportes financieros (gastos por rubros, pagos a proveedores) con un click. Exportá a Excel o PDF directamente desde el sistema.',
  },
  {
    color: 'cyan',
    icon: '/icons/desktop/svg.svg',
    title: 'App Móvil para Propietarios',
    desc: 'Disponible en iOS y Android. Los propietarios consultan sus expensas, reportan incidencias y reservan amenities con un click.',
  },
];

const KPIS = [
  { emoji: '🏆', value: '40+', label: 'Años de Experiencia' },
  { emoji: '🏢', value: 'SUELDOS', label: 'Sistema de sueldos Gratis' },
  { emoji: '👥', value: 'ATENCION', label: '100% Personalizada' },
  { emoji: '🇦🇷', value: '#1', label: 'Plataforma en Argentina' },
];

const ADA5_FEATURES = [
  { emoji: '📊', title: 'Dashboard en tiempo real', desc: 'Métricas y estados de cuenta' },
  { emoji: '🔔', title: 'Notificaciones', desc: 'Alertas automáticas de vencimientos' },
  { emoji: '📄', title: 'Documentos digitales', desc: 'Reglamentos, actas y comprobantes' },
];

const SLIDES = [
  {
    label: 'Menú de Inicio',
    bg: '/images/desktop/hero-carousel-1.png',
    overlays: [
      { src: '/images/desktop/hero-carousel-1-overlay-left.png', position: 'bottom-left' },
      { src: '/images/desktop/hero-carousel-1-overlay-right.png', position: 'bottom-right' },
    ],
    thumb: '/images/desktop/hero-carousel-1.png',
  },
  {
    label: 'Edificios',
    bg: '/images/desktop/hero-carousel-2.png',
    overlays: [],
    thumb: '/images/desktop/hero-carousel-2.png',
  },
  {
    label: 'Vistas',
    bg: '/images/desktop/hero-carousel-3.png',
    overlays: [],
    thumb: '/images/desktop/hero-carousel-3.png',
  },
  {
    label: 'Torres',
    bg: '/images/desktop/hero-carousel-4.png',
    overlays: [],
    thumb: '/images/desktop/hero-carousel-4.png',
  },
];

const ADA5WEB_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.loricode.Ada5Web';
const CER_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.loricode.ada5mobilepropietario';
const CER_APPLE_URL = 'https://apps.apple.com/ar/app/consorciosenred/id6762222689';

const PRODUCTS = [
  {
    img: '/images/desktop/home-product-ada5web.png',
    title: 'ADA5WEB',
    desc: 'Nuestro nuevo y renovado sistema de liquidación de expensas: hacé todo desde un solo lugar y desde cualquier lugar. Optimizá tus tiempos y aprovechá todas las funciones.',
  },
  {
    img: '/images/desktop/home-product-cer.png',
    title: 'ConsorciosEnRed+',
    desc: 'Desde acá el propietario puede ver sus expensas, recibos, comunicaciones realizadas, participar de votaciones, reservar amenities y avisar el pago de expensas.',
  },
  {
    img: '/images/desktop/home-product-ada-sueldos.png',
    title: 'ADA SUELDOS',
    desc: 'La plataforma de liquidación de sueldos más actualizada y rápida. Cargá las novedades, recibí los borradores y confirmalos desde un solo lugar.',
  },
];

export default function Home() {
  const [slide, setSlide] = useState(0);
  const total = SLIDES.length;
  const current = SLIDES[slide];

  usePageMeta({
    title: 'Sistemas Administrativos S.A. — Sistema Integral de Consorcios',
    description:
      'Sistema integral para la administración de consorcios. Más de 40 años desarrollando soluciones tecnológicas para administradores en Argentina.',
  });

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.querySelectorAll('.ia__media video').forEach((v) => v.pause());
    }
  }, []);

  const goPrev = () => setSlide((s) => (s - 1 + total) % total);
  const goNext = () => setSlide((s) => (s + 1) % total);

  return (
    <div className="home">
      {/* HERO */}
      <section className="hero">
        <div className="hero__inner container">
          <div className="hero__copy">
            <span className="hero__pill">
              <span className="hero__pill-dot" aria-hidden="true" />
              Más de 40 años de experiencia en la administración de consorcios
            </span>
            <h1 className="hero__title">
              Sistema Integral para la
              <br />
              Administración de Consorcios
            </h1>
            <p className="hero__subtitle">
              Nueva plataforma integrada para la gestión de consorcios, propietarios y empleados.
              <br />
              Simplificamos la administración de consorcios y barrios privados con la última tecnología y atención personalizada.
              <br />
              <br />
              Expertos en soluciones para consorcios.
            </p>
            <a className="hero__cta" href="#contacto">
              Solicitá tu demo
              <span aria-hidden="true">→</span>
            </a>
          </div>

          <div className="hero__visual">
            <div className="hero__mockup" aria-live="polite" aria-atomic="true">
              <img
                key={current.bg}
                className="hero__mockup-img"
                src={current.bg}
                alt={current.label}
              />
              {current.overlays.map((o) => (
                <img
                  key={o.src}
                  className={`hero__overlay hero__overlay--${o.position}`}
                  src={o.src}
                  alt=""
                  aria-hidden="true"
                />
              ))}
              <button
                type="button"
                className="hero__arrow hero__arrow--left"
                aria-label="Anterior"
                onClick={goPrev}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M10 3.33L5.33 8L10 12.67" stroke="#0A0A0A" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button
                type="button"
                className="hero__arrow hero__arrow--right"
                aria-label="Siguiente"
                onClick={goNext}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 3.33L10.67 8L6 12.67" stroke="#0A0A0A" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            <div className="hero__caption">
              <span className="hero__caption-label">{current.label}</span>
              <div className="hero__dots" aria-hidden="true">
                {SLIDES.map((_, idx) => (
                  <span
                    key={idx}
                    className={`hero__dot${idx === slide ? ' hero__dot--active' : ''}`}
                  />
                ))}
              </div>
            </div>

            <div className="hero__thumbs" role="tablist" aria-label="Miniaturas de slides">
              {SLIDES.map((s, idx) => (
                <button
                  key={s.thumb}
                  type="button"
                  role="tab"
                  className={`hero__thumb${idx === slide ? ' hero__thumb--active' : ''}`}
                  aria-label={`Ver ${s.label}`}
                  aria-selected={idx === slide}
                  onClick={() => setSlide(idx)}
                >
                  <img src={s.thumb} alt="" />
                </button>
              ))}
            </div>
          </div>
        </div>

        <svg
          className="hero__wave"
          aria-hidden="true"
          viewBox="0 0 1440 80"
          preserveAspectRatio="none"
        >
          <path d="M0,40 C240,0 480,0 720,40 C960,80 1200,80 1440,40 L1440,80 L0,80 Z" />
        </svg>
      </section>

      {/* KPIs */}
      <section className="kpis" aria-label="Indicadores">
        <dl className="kpis__inner">
          {KPIS.map((kpi) => (
            <div key={kpi.label} className="kpi">
              <span className="kpi__emoji" aria-hidden="true">{kpi.emoji}</span>
              <dt className="kpi__label">{kpi.label}</dt>
              <dd className="kpi__value">{kpi.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* FEATURES — TODO LO QUE NECESITAS */}
      <section className="features">
        <div className="container">
          <div className="features__pill">
            <span className="features__pill-dot" aria-hidden="true" />
            QUE NOS DESTACA?
          </div>
          <h2 className="features__title">TODO LO QUE NECESITÁS EN UN SOLO LUGAR</h2>
          <p className="features__subtitle">Una plataforma completa diseñada pensando en vos y en lo que tus consorcios necesitan</p>

          <div className="features__grid">
            {FEATURES.map((f) => (
              <article key={f.title} className={`feature-card feature-card--${f.color}`}>
                <div className="feature-card__head">
                  <div className="feature-card__icon">
                    <img src={f.icon} alt="" aria-hidden="true" />
                  </div>
                  <h3 className="feature-card__title">{f.title}</h3>
                </div>
                <p className="feature-card__desc">{f.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* IA INTEGRADA */}
      <section className="ia">
        <div className="container">
          <h2 className="ia__title">IMPLEMENTAMOS LA IA INTEGRADA</h2>

          <div className="ia__block">
            <div className="ia__media-col">
              <span className="ia__pill ia__pill--blue">LECTURA DEL EXTRACTO BANCARIO</span>
              <div className="ia__media">
                <video
                  src="/videos/identificacion-cobranzas.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  aria-label="Identificación automática de cobranzas"
                />
              </div>
            </div>
            <div className="ia__copy">
              <h3>Cargá tu extracto al sistema en formato XLS y relajate</h3>
              <p>
                Solo tenés que cargar en el sistema tu extracto bancario y nuestro motor identifica todas las cobranzas;
                podés asignar parámetros para la asignación automática y agilizar la identificación de los movimientos.
              </p>
              <ul className="ia__list">
                <li>Lectura automática del extracto</li>
                <li>Identificación masiva de cobranzas</li>
              </ul>
            </div>
          </div>

          <div className="ia__block ia__block--reverse">
            <div className="ia__copy">
              <h3>Automatizá tus procesos</h3>
              <p>
                Cargá tus facturas y dejá que el sistema haga el resto. Con la IA de Sistemas Administrativos
                solo necesitás subir las facturas (incluso de varios consorcios a la vez) y dejar que el sistema
                las reconozca, las asigne a los proveedores correspondientes y cargue los gastos.
                Optimizá tus tiempos para trabajar menos y disfrutar de las cosas que importan.
              </p>
            </div>
            <div className="ia__media-col">
              <span className="ia__pill ia__pill--green">LECTURA DE FACTURAS CON IA</span>
              <div className="ia__media">
                <video
                  src="/videos/whatsapp-video-2026-03-06.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  aria-label="Lectura de facturas con IA"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* APPS — 2 apps con disponibilidad */}
      <section className="apps-section">
        <div className="container">
          <span className="apps-section__pill">APPS MÓVILES</span>
          <h2 className="apps-section__title">2 NUEVAS APPS, PARA EL ADMINISTRADOR Y PARA EL PROPIETARIO</h2>

          <div className="apps-section__grid">
            {/* IZQUIERDA — ConsorciosEnRed+ */}
            <article className="app-card app-card--side">
              <h3 className="app-card__name">ConsorciosEnRed+</h3>
              <div className="app-card__visual">
                <img src="/images/desktop/home-apps-cer-mockup.png" alt="ConsorciosEnRed+ en iPhone" />
              </div>
              <p className="app-card__avail">Disponible en:</p>
              <StoreBadges appStoreUrl={CER_APPLE_URL} googlePlayUrl={CER_PLAY_URL} />
            </article>

            {/* CENTRO — descripción + features + CTA */}
            <article className="app-card app-card--center">
              <p className="app-card__desc">
                Con nuestra app disponible para iOS y Android, tanto administradores como propietarios
                tienen todo el control al alcance de la mano, las 24 horas del día.
              </p>

              <ul className="app-card__features">
                {ADA5_FEATURES.map((af) => (
                  <li key={af.title}>
                    <span className="app-card__features-emoji" aria-hidden="true">{af.emoji}</span>
                    <div className="app-card__features-text">
                      <strong>{af.title}</strong>
                      <span>{af.desc}</span>
                    </div>
                  </li>
                ))}
              </ul>

              <a className="app-card__cta" href="/apps">
                Conocé Más
                <span aria-hidden="true">→</span>
              </a>
            </article>

            {/* DERECHA — ADA5WEB */}
            <article className="app-card app-card--side">
              <h3 className="app-card__name">ADA5WEB</h3>
              <div className="app-card__visual">
                <img src="/images/desktop/home-apps-ada5web-mockup.png" alt="ADA5WEB en celular" />
              </div>
              <p className="app-card__avail">Disponible en:</p>
              <StoreBadges appStoreUrl={null} googlePlayUrl={ADA5WEB_PLAY_URL} />
            </article>
          </div>
        </div>
      </section>

      {/* NUESTROS PRODUCTOS */}
      <section className="products">
        <div className="container">
          <h2 className="products__title">NUESTROS PRODUCTOS</h2>

          <div className="products__container">
            <div className="products__grid">
              {PRODUCTS.map((p) => (
                <article key={p.title} className="product-card">
                  <div className="product-card__img">
                    <img src={p.img} alt={p.title} />
                  </div>
                  <h3 className="product-card__title">{p.title}</h3>
                  <p className="product-card__desc">{p.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CONTACTO */}
      <section className="contact" id="contacto">
        <div className="container contact__inner">
          <div className="contact__visual">
            <img src="/images/desktop/header-logo.png" alt="" />
          </div>

          <div className="contact__form">
            <h2 className="contact__title">¿Sos Administrador?</h2>
            <p className="contact__subtitle">Si te interesan nuestros servicios, contactate con nosotros.</p>
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
}
